import { removeEffectFamily } from '../effects/effectLifecycle.mjs';

// Turn timeline core shared by the client and the server end-turn command: the combat state shape, events that
// expire effects, undo snapshot compaction and forward turn advance. Pure: no React, persistence or presentation.

/** Resolve the persisted identity used by both character and enemy combatants.
 * Enemy roster rows have enemyId, while sanitized combat participants use
 * characterId and active effects use targetCombatantId. */
export const getCanonicalCombatantIdentity = (value) => {
  if (typeof value === 'string' || typeof value === 'number') {
    const normalized = String(value).trim();
    return normalized || null;
  }
  if (!value || typeof value !== 'object') return null;
  const identity = value.targetCombatantId ?? value.combatantId ?? value.participantId ??
    value.characterId ?? value.enemyId ?? value.tokenId ?? value.sourceId ?? value.id ?? value._id;
  return getCanonicalCombatantIdentity(identity);
};

export const createCombatState = (state = {}) => ({
  ...(Object.prototype.hasOwnProperty.call(state,'manualOrder')?{manualOrder:state.manualOrder===true}:{}),
  ...(Number.isInteger(state.initiativeRevision)?{initiativeRevision:state.initiativeRevision}:{}),
  ...(Number.isInteger(state.orderRevision)?{orderRevision:state.orderRevision}:{}),
  participants: Array.isArray(state.participants) ? state.participants : [],
  activeTurn: Number.isInteger(state.activeTurn) ? state.activeTurn : null,
  round: Math.max(1, Number.isInteger(state.round) ? state.round : 1),
  turnSequence: Math.max(0, Number.isInteger(state.turnSequence) ? state.turnSequence : 0),
  activeEffects: Array.isArray(state.activeEffects) ? state.activeEffects : [],
  eventLog: Array.isArray(state.eventLog) ? state.eventLog : [],
  undoStack: Array.isArray(state.undoStack) ? state.undoStack : [],
  turnFeatureUsage: state.turnFeatureUsage && typeof state.turnFeatureUsage === 'object' && !Array.isArray(state.turnFeatureUsage) ? state.turnFeatureUsage : {},
  reactionStates: Array.isArray(state.reactionStates) ? state.reactionStates : [],
  reactionOpportunities: Array.isArray(state.reactionOpportunities) ? state.reactionOpportunities : [],
  pendingDecisions: Array.isArray(state.pendingDecisions) ? state.pendingDecisions : [],
  resolvedDecisionIds: Array.isArray(state.resolvedDecisionIds) ? state.resolvedDecisionIds : [],
  ...(Array.isArray(state.extraTurns) && state.extraTurns.length ? { extraTurns: state.extraTurns } : {}),
});

export const eventFor = (type, state, combatantId) => ({
  type,
  ...(combatantId ? { combatantId } : {}),
  ...(!['combatEnded'].includes(type) ? { round: state.round } : {}),
  ...(type === 'turnStarted' || type === 'turnEnded'
    ? { turnSequence: state.turnSequence }
    : {}),
});

const expirationMatches = (effect, event) => {
  const expiration = effect.expiration || {};
  if (expiration.type === 'combatEnd') return event.type === 'combatEnded';
  if (expiration.type === 'rounds') {
    const type = expiration.boundary === 'end' ? 'roundEnded' : 'roundStarted';
    // An end boundary immediately precedes the next numbered round. Thus an
    // effect expiring at round 11 ends with round 10, rather than surviving
    // through an eleventh numbered round.
    const expirationEventRound = expiration.boundary === 'end'
      ? expiration.expiresAtRound - 1
      : expiration.expiresAtRound;
    return event.type === type && event.round >= expirationEventRound;
  }
  if (expiration.type !== 'sourceTurn' && expiration.type !== 'targetTurn') return false;
  const type = expiration.boundary === 'end' ? 'turnEnded' : 'turnStarted';
  return event.type === type && event.combatantId === expiration.combatantId &&
    (expiration.expireOnCurrentTurn
      ? event.turnSequence >= (effect.appliedAtTurnSequence ?? -1)
      : event.turnSequence > (effect.appliedAtTurnSequence ?? -1));
};

/** Resolve all effects as a batch; ids make simultaneous expiration deterministic. */
export const resolveCombatEvent = (effects, event) => {
  const expiredEffectIds = [];
  const activeEffects = [...(effects || [])].sort((a, b) => String(a.id).localeCompare(String(b.id))).flatMap((effect) => {
    if (effect.expiration?.type === 'elapsedRounds' && event.type === 'roundEnded') {
      const remainingRounds = effect.expiration.remainingRounds - 1;
      if (remainingRounds > 0) return [{ ...effect, expiration: { ...effect.expiration, remainingRounds } }];
      expiredEffectIds.push(effect.id); return [];
    }
    if (!expirationMatches(effect, event)) return [effect];
    const expiration = effect.expiration;
    if (expiration.type === 'sourceTurn' || expiration.type === 'targetTurn') {
      const remaining = Math.max(0, (expiration.remainingOccurrences || 1) - 1);
      if (remaining > 0) return [{ ...effect, expiration: { ...expiration, remainingOccurrences: remaining } }];
    }
    expiredEffectIds.push(effect.id);
    return [];
  });
  const survivingEffects = removeEffectFamily(activeEffects, expiredEffectIds);
  const survivingIds = new Set(survivingEffects.map((effect) => effect.id));
  return { activeEffects: survivingEffects, expiredEffectIds: [
    ...expiredEffectIds, ...activeEffects.filter((effect) => !survivingIds.has(effect.id)).map((effect) => effect.id),
  ] };
};

export const emit = (state, event) => {
  const resolved = resolveCombatEvent(state.activeEffects, event);
  return { ...state, activeEffects: resolved.activeEffects, eventLog: [...state.eventLog, event], lastExpiredEffectIds: resolved.expiredEffectIds };
};

// Undo logs share their unchanged prefix with the preceding snapshot. The first
// retained snapshot always owns a complete log, so trimming history is safe.
export const expandUndoStack = (stack) => {
  let previousLog = [];
  return stack.map(snapshot => {
    let eventLog = Array.isArray(snapshot.eventLog) ? snapshot.eventLog : [];
    if (!Array.isArray(snapshot.eventLog) && snapshot.eventLogDelta) {
      const { start = 0, prefixLength, events } = snapshot.eventLogDelta;
      if (!Number.isInteger(start) || start < 0 || !Number.isInteger(prefixLength) || prefixLength < 0 || start + prefixLength > previousLog.length || !Array.isArray(events)) {
        throw new Error('Invalid undo event history.');
      }
      eventLog = [...previousLog.slice(start, start + prefixLength), ...events];
    }
    previousLog = eventLog;
    const restored = { ...snapshot, eventLog };
    delete restored.eventLogDelta;
    delete restored.undoStack;
    return restored;
  });
};

export const compactUndoStack = (stack = []) => {
  const expanded = expandUndoStack(stack).slice(-20);
  let previousLog = [];
  return expanded.map((snapshot, index) => {
    const log = snapshot.eventLog;
    const previousKeys = previousLog.map(event => JSON.stringify(event));
    const keys = log.map(event => JSON.stringify(event));
    let start = 0; let prefixLength = 0;
    // The server caps the live log at 1,000 events. Share a shifted window too,
    // so dropping old events does not reintroduce 20 full copies of history.
    for (let offset = 0; offset < previousKeys.length; offset++) {
      if (previousKeys[offset] !== keys[0]) continue;
      let length = 0;
      while (length < Math.min(previousKeys.length - offset, keys.length) && previousKeys[offset + length] === keys[length]) length++;
      if (length > prefixLength) { start = offset; prefixLength = length; }
      if (prefixLength === keys.length) break;
    }
    const delta = { start, prefixLength, events: log.slice(prefixLength) };
    previousLog = log;
    if (!index || JSON.stringify(delta).length + 20 >= JSON.stringify(log).length) return snapshot;
    const compact = { ...snapshot, eventLogDelta: delta };
    delete compact.eventLog;
    return compact;
  });
};

/**
 * Thief's Reflexes (Thief 17): two turns in the first round, the second at Initiative minus 10. A combatant stays one
 * participant; `extraTurns` lists `{ characterId, status: 'pending' }` and the turn jumps back to it once the next
 * combatant is below that count (or the round would end), then resumes where it left off (`status: 'active'`, with
 * `resumeIndex` and `resumeWraps`). A DM's jump to a chosen combatant leaves pending turns alone.
 */
const nextWithExtraTurns = (state, current) => {
  const count = state.participants.length;
  const extras = state.extraTurns || [];
  const running = extras.find(extra => extra.status === 'active');
  const natural = running ? Math.min(running.resumeIndex, count - 1) : (state.activeTurn === null ? 0 : (state.activeTurn + 1) % count);
  const wraps = running ? running.resumeWraps === true : Boolean(current) && natural <= state.activeTurn;
  const waiting = extras.filter(extra => extra.status === 'pending' && state.participants.some(item => item.characterId === extra.characterId));
  const initiativeOf = id => Number(state.participants.find(item => item.characterId === id)?.initiative) - 10;
  const due = state.round === 1 && current ? waiting.sort((a, b) => initiativeOf(b.characterId) - initiativeOf(a.characterId))
    .find(extra => wraps || Number(state.participants[natural].initiative) < initiativeOf(extra.characterId)) : null;
  const rest = extras.filter(extra => extra !== running && extra !== due);
  if (due) {
    return { nextIndex: state.participants.findIndex(item => item.characterId === due.characterId), wrapped: false,
      extraTurns: [...rest, { characterId: due.characterId, status: 'active', resumeIndex: natural, resumeWraps: wraps }] };
  }
  return { nextIndex: natural, wrapped: wraps, extraTurns: wraps ? [] : rest };
};

export const advanceTurn = (inputState, { nextCombatantId } = {}) => {
  let state = createCombatState(inputState);
  if (!state.participants.length) return state;
  const snapshot = { ...state, undoStack: undefined };
  state = { ...state, undoStack: compactUndoStack([...state.undoStack, snapshot]) };
  const current = state.participants[state.activeTurn];
  if (current) state = emit(state, eventFor('turnEnded', state, current.characterId));
  else state = emit(state, eventFor('roundStarted', state));
  const planned = nextCombatantId ? null : nextWithExtraTurns(state, current);
  const nextIndex = nextCombatantId ? state.participants.findIndex((item) => item.characterId === nextCombatantId) : planned.nextIndex;
  if (nextIndex < 0) throw new Error('Next combatant is not in combat');
  const wrapped = Boolean(planned?.wrapped);
  if (planned) {
    const { extraTurns, ...rest } = state;
    state = { ...rest, ...(planned.extraTurns.length ? { extraTurns: planned.extraTurns } : {}) };
  }
  if (wrapped) {
    state = emit(state, eventFor('roundEnded', state));
    state = { ...state, round: state.round + 1 };
    state = emit(state, eventFor('roundStarted', state));
  }
  state = { ...state, activeTurn: nextIndex, turnSequence: state.turnSequence + 1 };
  const startingCombatantId = state.participants[nextIndex].characterId;
  state = { ...state, reactionStates: state.reactionStates.filter((reaction) => reaction.combatantId !== startingCombatantId) };
  return emit(state, eventFor('turnStarted', state, state.participants[nextIndex].characterId));
};
